import moment, { unitOfTime } from 'moment';
import { Multiplicator, MultiplicatorDTO, MultiplicatorHoursDTO } from '../loyalties/type';
import { Day, OpeningHours, OpeningWeek } from './models/openingWeek.Model';
import { AddressDTO, HoursDTO, StoreHoursDTO } from './type';

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const formatHoursToDTO = ({ openWeeks }: { openWeeks: OpeningWeek[]; closeWeek: OpeningWeek }) => {
  // we use reduce to sort day
  return DAYS.reduce((acc: StoreHoursDTO['hours'], day, index) => {
    for (const week of openWeeks) {
      if (week.week[index].isSelected) {
        acc[day] = week.hours;
        return acc;
      }
    }
    acc[day] = [];
    return acc;
  }, {});
};

export const formatMultiplicatorToDTO = (multiplicators: Multiplicator[]) => {
  return multiplicators.map(multiplicator => {
    return {
      coefficient: multiplicator.coefficient,
      storeIds: multiplicator.storeIds,
      slots: multiplicator.slots
        .filter((slot: Day) => slot.isSelected)
        .map((slot: Day) => ({
          day: slot.shortName,
          hours: multiplicator.hours.map((hour: OpeningHours) => ({
            start: moment(hour.open).format('HH:mm'),
            end: moment(hour.close).format('HH:mm'),
          })),
        })),
    };
  });
};

const isWeekEmpty = (week: OpeningWeek): boolean => {
  return !week.hours.length;
};

const isSameMoment = (
  weekHours: OpeningHours,
  hoursDTO: OpeningHours,
  granularities: unitOfTime.StartOf[],
): boolean => {
  for (const granularity of granularities) {
    if (
      !(
        moment(weekHours.open).isSame(hoursDTO.open, granularity) &&
        moment(weekHours.close).isSame(hoursDTO.close, granularity)
      )
    ) {
      return false;
    }
  }
  return true;
};

const weekWithSameHours = (weeks: OpeningWeek[], hoursDTO: HoursDTO[]): OpeningWeek | null => {
  for (const week of weeks) {
    const weekHours = week.hours;
    if (weekHours.length > 0 && weekHours.length === hoursDTO.length) {
      for (let i = 0; i < weekHours.length; i += 1) {
        if (isSameMoment(weekHours[i], hoursDTO[i], ['minute', 'hour'])) {
          return week;
        }

        if (weekHours[i].open === hoursDTO[i].open && weekHours[i].close === hoursDTO[i].close) {
          return week;
        }
      }
    }
  }
  return null;
};

// Mutates the week it is given, so callers must pass a locally generated one: `hours` comes from
// redux, which immer freezes in production too.
const addHoursToWeek = (day: string, openWeek: OpeningWeek, hours: StoreHoursDTO['hours']): void => {
  const found = openWeek.week.find(d => d.shortName === day);
  if (found) {
    found.isSelected = true;
  }
  openWeek.hours = [...hours[day]];
};

export const generateWeek = (id: string): OpeningWeek => {
  const week = DAYS.map((day, index) => ({
    id: `${id}${index}`,
    shortName: day as Day['shortName'],
    isSelected: false,
  }));
  return { id, week, hours: [] };
};

export const generateMultiplicator = (id: string, storeIds?: string[]): Multiplicator => {
  const slots = DAYS.map((day, index) => ({
    id: `${id}${index}`,
    shortName: day as Day['shortName'],
    isSelected: false,
  }));
  return { id, storeIds: storeIds || [], coefficient: 2, slots, hours: [getDefaultHoursSlot()] };
};

export const getDefaultHoursSlot = () => {
  const DEFAULT_BEGIN_HOUR = moment().hours(8).minutes(0).seconds(0).toISOString();
  const DEFAULT_END_HOUR = moment().hours(18).minutes(0).seconds(0).toISOString();
  return { open: DEFAULT_BEGIN_HOUR, close: DEFAULT_END_HOUR };
};

export const formatDTOToHours = (hoursDTO?: StoreHoursDTO) => {
  const openWeekModel = generateWeek('o');
  const closeWeek = generateWeek('c');
  const openWeeks: OpeningWeek[] = [openWeekModel];

  if (!hoursDTO) {
    const h = getDefaultHoursSlot();
    openWeekModel.hours.push(h);
    return { openWeeks, closeWeek };
  }

  const { hours } = hoursDTO;
  Object.keys(hours).forEach((dayName: string) => {
    if (hours[dayName].length) {
      for (const openWeek of openWeeks) {
        if (isWeekEmpty(openWeek)) {
          addHoursToWeek(dayName, openWeek, hours);
        }
        const week = weekWithSameHours(openWeeks, hours[dayName]);
        if (week) {
          addHoursToWeek(dayName, week, hours);
        } else {
          const newOpenWeek = generateWeek(`o${openWeeks.length}`);
          addHoursToWeek(dayName, newOpenWeek, hours);
          openWeeks.push(newOpenWeek);
        }
        break;
      }
    } else {
      for (const closeDay of closeWeek.week) {
        if (closeDay.shortName === dayName) {
          closeDay.isSelected = true;
        }
      }
    }
  });
  return { openWeeks, closeWeek };
};

export const formatAddress = (addressDto: AddressDTO) => {
  return `${addressDto.line1}, ${addressDto.city}`;
};

export const formatDTOToMultiplicators = (multiplicatorsDTO?: MultiplicatorDTO[]) => {
  if (!multiplicatorsDTO) {
    return [];
  }
  return multiplicatorsDTO.map(multiplicatorDTO => {
    const selectedDays = new Set(multiplicatorDTO.slots.map(slot => slot.day));

    const multiplicatorId = `${multiplicatorsDTO.indexOf(multiplicatorDTO)}`;

    const slots: Day[] = DAYS.map((day, index) => ({
      id: `${multiplicatorId}${index}`,
      shortName: day as Day['shortName'],
      isSelected: selectedDays.has(day as Day['shortName']),
    }));

    const hours: OpeningHours[] = multiplicatorDTO.slots[0]?.hours.map((hourDTO: MultiplicatorHoursDTO) => ({
      open: moment(`${moment().format('YYYY-MM-DD')} ${hourDTO.start}`, 'YYYY-MM-DD HH:mm').toISOString(),
      close: moment(`${moment().format('YYYY-MM-DD')} ${hourDTO.end}`, 'YYYY-MM-DD HH:mm').toISOString(),
    })) ?? [getDefaultHoursSlot()];

    return {
      id: `${multiplicatorId}`,
      storeIds: multiplicatorDTO.storeIds,
      coefficient: multiplicatorDTO.coefficient,
      slots,
      hours,
    };
  });
};
